import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, Calendar, Pill, AlertTriangle } from 'lucide-react';
import { getReports, getCommitteePacks, downloadCommitteePackPdf } from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const reportTypes = [
  { id: 'executive', name: 'Executive Procurement Report', description: 'High-level summary for leadership' },
  { id: 'substitution', name: 'Molecule Substitution Review', description: 'Detailed substitution analysis' },
  { id: 'savings', name: 'High Savings Opportunity Report', description: 'Cost reduction opportunities' },
  { id: 'evidence', name: 'Evidence Gap Report', description: 'Areas needing more research' },
  { id: 'geography', name: 'Geography Availability Report', description: 'Regional market analysis' }
];

export default function ReportsPage() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPack, setDownloadingPack] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch reports and packs separately to handle individual failures
        const [reportsRes, packsRes] = await Promise.allSettled([
          getReports(),
          getCommitteePacks()
        ]);
        
        // Handle reports result
        if (reportsRes.status === 'fulfilled') {
          setReports(reportsRes.value.data.reports || []);
        } else {
          console.log('Reports endpoint not available');
          setReports([]);
        }
        
        // Handle packs result
        if (packsRes.status === 'fulfilled') {
          setPacks(packsRes.value.data.packs || []);
        } else {
          console.error('Failed to fetch committee packs:', packsRes.reason);
          setPacks([]);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleDownloadPack = async (pack) => {
    setDownloadingPack(pack.id);
    try {
      const res = await downloadCommitteePackPdf(pack.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Committee_Pack_${pack.drug?.brand_name || 'Drug'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPack(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReportTypeName = (type) => {
    return reportTypes.find(r => r.id === type)?.name || type;
  };

  return (
    <div className="p-6" data-testid="reports-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">Reports</h1>
        <p className="text-slate-500">Generate and access procurement intelligence reports</p>
      </div>

      {/* Report Types */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Available Report Types</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reportTypes.map(type => (
            <div key={type.id} className="card-premium p-5" data-testid={`report-type-${type.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-slate-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{type.name}</h3>
                  <p className="text-sm text-slate-500 mt-1">{type.description}</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-4">
                Select a molecule from search to generate this report
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Committee Packs */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Pharmacy Committee Packs</h2>
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="card-premium p-4">
                <div className="skeleton h-16 rounded-lg" />
              </div>
            ))}
          </div>
        ) : packs.length > 0 ? (
          <div className="space-y-3">
            {packs.map(pack => (
              <div key={pack.id} className="card-premium p-4" data-testid={`committee-pack-${pack.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900">
                        {pack.drug?.brand_name} ({pack.drug?.generic_name})
                      </h3>
                      <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(pack.generated_at)}
                        </span>
                        <span>{pack.generics?.length || 0} generics evaluated</span>
                        <span className="text-emerald-600 font-medium">
                          ${(pack.financial_impact?.total_potential_savings || 0).toLocaleString()} potential savings
                        </span>
                      </div>
                    </div>
                  </div>
                  <button 
                    className="btn-secondary flex items-center gap-2"
                    onClick={() => handleDownloadPack(pack)}
                    disabled={downloadingPack === pack.id}
                    data-testid={`download-pack-${pack.id}`}
                  >
                    <Download className="w-4 h-4" />
                    {downloadingPack === pack.id ? 'Downloading...' : 'Download PDF'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card-premium p-8 text-center">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No committee packs generated yet</p>
            <p className="text-sm text-slate-500 mt-1">
              Generate packs from any molecule detail page using the "Committee Pack PDF" button
            </p>
          </div>
        )}
      </div>

      {/* Recent Reports */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Recent Reports</h2>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="card-premium p-4">
                <div className="flex items-center gap-4">
                  <div className="skeleton w-10 h-10 rounded-lg" />
                  <div className="flex-1">
                    <div className="skeleton h-5 w-48 mb-1" />
                    <div className="skeleton h-4 w-32" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : reports.length > 0 ? (
          <div className="space-y-3">
            {reports.map(report => (
              <div key={report.id} className="card-premium p-4" data-testid={`report-${report.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#0A2540]/10 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-[#0A2540]" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900">
                        {getReportTypeName(report.type)}: {report.drug?.brand_name}
                      </h3>
                      <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(report.generated_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Pill className="w-4 h-4" />
                          {report.generics?.length || 0} generics
                        </span>
                      </div>
                    </div>
                  </div>
                  <button className="btn-secondary flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Export
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card-premium p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 font-medium">No reports generated yet</p>
            <p className="text-sm text-slate-500 mt-1">
              Generate reports from molecule detail pages
            </p>
            <Link to="/search" className="btn-primary mt-4 inline-flex items-center gap-2">
              <Pill className="w-4 h-4" />
              Search Molecules
            </Link>
          </div>
        )}
      </div>

      {/* Disclaimer */}
      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">Confidential Reports</p>
          <p className="text-sm text-amber-700">
            Reports contain proprietary analysis and should be treated as confidential. Distribution should follow institutional data governance policies.
          </p>
        </div>
      </div>
    </div>
  );
}
